/**
 * CustomRoundModal Constants
 * 
 * Centralized constants for styling and configuration
 * Uses Ant Design CSS variables for consistency with the application theme
 */

/**
 * Modal layout configuration
 */
export const MODAL_CONFIG = {
  width: "95vw",
  maxWidth: 1600,
  minHeight: "90vh",
} as const;

/**
 * Column width percentages
 */
export const COLUMN_WIDTHS = {
  column1: "25%", // AI Template Generator
  column2: "47%", // Round Form
  column3: "28%", // Sample Questions
} as const;

/**
 * Skills list configuration
 */
export const SKILLS_LIST_CONFIG = {
  maxHeight: 200,
  borderRadius: "var(--ant-border-radius, 6px)",
} as const;

/**
 * Question types available for selection
 */
export const QUESTION_TYPES = [
  { value: "MCQ", label: "MCQ" },
  { value: "Descriptive", label: "Descriptive" },
  { value: "Coding", label: "Coding" },
  { value: "Mixed", label: "Mixed" },
] as const;

/**
 * Difficulty levels with emoji indicators
 */
export const DIFFICULTY_LEVELS = [
  { value: "Beginner", label: "Beginner" },
  { value: "Intermediate", label: "Intermediate" },
  { value: "Advanced", label: "Advanced" },
  { value: "Expert", label: "Expert" },
] as const;

/**
 * Mapping from display question type to form answerType
 */
export const QUESTION_TYPE_TO_ANSWER_TYPE: Record<string, string> = {
  "MCQ": "mcq",
  "Descriptive": "descriptive",
  "Coding": "coding",
  "Mixed": "mixed",
};

/**
 * Mapping from answerType to display question type
 */
export const ANSWER_TYPE_TO_QUESTION_TYPE: Record<string, string> = {
  "mcq": "MCQ",
  "descriptive": "Descriptive",
  "coding": "Coding",
  "mixed": "Mixed",
};

/**
 * Valid question types for form validation
 */
export const VALID_QUESTION_TYPES = ["mcq", "descriptive", "coding", "mixed"] as const;

/**
 * Shared styles using Ant Design CSS variables
 * These ensure consistency with the overall application theme
 */
export const SHARED_STYLES = {
  // Section backgrounds
  sectionBackground: "var(--ant-color-bg-layout, #f0f2f5)",
  containerBackground: "var(--ant-color-bg-container, #ffffff)",
  
  // Borders
  border: "1px solid var(--ant-color-border, #d9d9d9)",
  borderColor: "var(--ant-color-border, #d9d9d9)",
  borderRadius: "var(--ant-border-radius, 6px)",
  borderRadiusLg: "var(--ant-border-radius-lg, 8px)",
  
  // Colors - Primary
  primaryColor: "var(--ant-color-primary, #1890ff)",
  primaryBg: "var(--ant-color-primary-bg, #e6f7ff)",
  primaryBgHover: "var(--ant-color-primary-bg-hover, #bae7ff)",
  
  // Colors - Success
  successColor: "var(--ant-color-success, #52c41a)",
  successBg: "var(--ant-color-success-bg, #f6ffed)",
  successBorder: "var(--ant-color-success-border, #b7eb8f)",
  
  // Colors - Warning
  warningColor: "var(--ant-color-warning, #faad14)",
  warningBg: "var(--ant-color-warning-bg, #fffbe6)",
  warningBorder: "var(--ant-color-warning-border, #ffe58f)",
  
  // Colors - Text
  textColor: "var(--ant-color-text, rgba(0, 0, 0, 0.88))",
  textSecondary: "var(--ant-color-text-secondary, rgba(0, 0, 0, 0.65))",
  textTertiary: "var(--ant-color-text-tertiary, rgba(0, 0, 0, 0.45))",
  textQuaternary: "var(--ant-color-text-quaternary, rgba(0, 0, 0, 0.25))",
  
  // Spacing
  paddingSm: "var(--ant-padding-sm, 12px)",
  paddingMd: "var(--ant-padding-md, 16px)",
  paddingLg: "var(--ant-padding-lg, 24px)",
  marginSm: "var(--ant-margin-sm, 12px)",
  marginMd: "var(--ant-margin-md, 16px)",
  marginLg: "var(--ant-margin-lg, 24px)",
  
  // Typography
  fontSizeBase: "var(--ant-font-size, 14px)",
  fontSizeSm: "var(--ant-font-size-sm, 12px)",
  fontSizeLg: "var(--ant-font-size-lg, 16px)",
  
  // Shadows
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  boxShadowHover: "0 4px 12px rgba(0, 0, 0, 0.12)",
  columnShadow: "0 -4px 6px -4px rgba(0, 0, 0, 0.15)",
} as const;

/**
 * Column-specific styles
 */
export const COLUMN_STYLES = {
  // Column container base styles
  base: {
    display: "flex",
    flexDirection: "column" as const,
    height: "100%",
  },
  
  // Column 1 - AI Template Generator
  column1: {
    flex: `0 0 ${COLUMN_WIDTHS.column1}`,
    borderRight: SHARED_STYLES.border,
  },
  
  // Column 2 - Round Form
  column2: {
    flex: `0 0 ${COLUMN_WIDTHS.column2}`,
    borderRight: SHARED_STYLES.border,
  },
  
  // Column 3 - Sample Questions
  column3: {
    flex: `0 0 ${COLUMN_WIDTHS.column3}`,
  },
  
  // Header wrapper - contains content and progress bar
  headerWrapper: {
    flexShrink: 0,
    borderBottom: SHARED_STYLES.border,
  },
  
  // Header content styles (padding without border)
  header: {
    padding: SHARED_STYLES.paddingMd,
  },
  
  // Scrollable content area
  scrollableContent: {
    flex: 1,
    minHeight: 0,
    overflowY: "auto" as const,
    overflowX: "hidden" as const,
    padding: SHARED_STYLES.paddingMd,
    scrollbarWidth: "thin" as const,
    // Use CSS variables for scrollbar colors
    scrollbarColor: "var(--ant-color-text-quaternary, rgba(0, 0, 0, 0.25)) var(--ant-color-bg-layout, #f0f2f5)",
  },
  
  // Footer styles
  footer: {
    flexShrink: 0,
    padding: SHARED_STYLES.paddingMd,
    marginTop: "auto",
    borderTop: SHARED_STYLES.border,
    backgroundColor: SHARED_STYLES.containerBackground,
  },
} as const;

/**
 * Form section styles
 */
export const FORM_SECTION_STYLES = {
  container: {
    background: SHARED_STYLES.sectionBackground,
    padding: SHARED_STYLES.paddingMd,
    borderRadius: SHARED_STYLES.borderRadiusLg,
    marginBottom: SHARED_STYLES.marginMd,
  },
  
  lastContainer: {
    background: SHARED_STYLES.sectionBackground,
    padding: SHARED_STYLES.paddingMd,
    borderRadius: SHARED_STYLES.borderRadiusLg,
    marginBottom: 0,
  },
  
  title: {
    marginBottom: 12,
    marginTop: 0,
  },
} as const;

/**
 * Skills selector styles
 */
export const SKILLS_SELECTOR_STYLES = {
  listContainer: {
    maxHeight: SKILLS_LIST_CONFIG.maxHeight,
    overflowY: "auto" as const,
    border: SHARED_STYLES.border,
    borderRadius: SHARED_STYLES.borderRadius,
    backgroundColor: SHARED_STYLES.containerBackground,
  },
  
  listItem: (isSelected: boolean) => ({
    cursor: "pointer",
    padding: "8px 12px",
    backgroundColor: isSelected 
      ? SHARED_STYLES.primaryBg 
      : "transparent",
    borderBottom: SHARED_STYLES.border,
    transition: "background-color 0.2s ease",
  }),
  
  inputContainer: {
    marginTop: SHARED_STYLES.marginSm,
  },
} as const;

/**
 * Radio group button styles for Question Type and Difficulty
 */
export const RADIO_GROUP_STYLES = {
  buttonStyle: "solid" as const,
  optionType: "button" as const,
  style: {
    width: "100%",
  },
} as const;

/**
 * Column status type for progress indicators
 */
export type ColumnStatus = "todo" | "inProgress" | "completed";

/**
 * Progress indicator styles for column headers
 * Replaces the bottom border with a visual status indicator
 */
export const PROGRESS_INDICATOR_STYLES = {
  // Container for the progress bar
  container: {
    height: 3,
    width: "100%",
    backgroundColor: "var(--ant-color-border, #d9d9d9)",
    overflow: "hidden",
  },
  // The animated bar inside
  bar: {
    height: "100%",
    transition: "width 0.3s ease, background-color 0.3s ease",
  },
  // Status-specific styles
  todo: {
    width: "0%",
    backgroundColor: "var(--ant-color-border, #d9d9d9)",
  },
  inProgress: {
    width: "50%",
    backgroundColor: "var(--ant-color-warning, #faad14)",
  },
  completed: {
    width: "100%",
    backgroundColor: "var(--ant-color-success, #52c41a)",
  },
} as const;

